
import { useState } from "react";
import Layout from "@/components/layout/Layout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { motion } from "framer-motion";

const faqs = [
  {
    question: "How many lessons do I need to take?",
    answer: "The number of driving lessons needed varies per individual. On average, learners take between 30-40 hours of professional instruction, combined with 20 hours of private practice. Our instructors will evaluate your progress and advise on the ideal number of lessons for you."
  },
  {
    question: "What documents do I need to bring to my first driving lesson?",
    answer: "Please bring your provisional driving license. Without it, the instructor cannot conduct your lesson. It's also advisable to bring any eyewear you normally use for distance vision."
  },
  {
    question: "Can I take lessons if I have a medical condition?",
    answer: "In most cases, yes. However, certain medical conditions must be reported to the DVLA. Our instructors can provide guidance based on your specific situation. Safety is our priority, so please disclose any medical conditions that might affect your driving."
  },
  {
    question: "How do I book a driving test?",
    answer: "Driving tests can be booked through the official government website or by phone. You'll need your UK driving license number, theory test pass certificate, and payment for the test fee. Our instructors can guide you through this process or book it on your behalf if requested."
  },
  {
    question: "What happens if I fail my driving test?",
    answer: "If you don't pass your test, don't worry – it's common and not a reflection of your future driving ability. You'll receive feedback on areas to improve. After a mandatory 10-day waiting period, you can rebook your test. Our instructors will help you address the issues from your first test before retaking it."
  },
  {
    question: "How long is a driving lesson?",
    answer: "Our standard driving lessons are 1 hour or 2 hours long. We also offer intensive courses which may involve longer daily sessions. Research shows that 2-hour lessons can be more beneficial as they provide more time to practice and develop skills."
  },
  {
    question: "Do you offer automatic driving lessons?",
    answer: "Yes, we offer lessons in both manual and automatic vehicles. Automatic lessons are great for those who struggle with clutch control or have certain disabilities. Remember that passing in an automatic car will restrict your license to automatic vehicles only."
  },
  {
    question: "How do I pay for my lessons?",
    answer: "We accept payments via credit/debit card, bank transfer, or through our online payment system. You can pay for individual lessons or purchase a block of lessons at a discounted rate. All payments are secure and receipts are provided."
  }
];

const FAQ = () => {
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>("item-0");

  return (
    <Layout>
      <div className="container mx-auto py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about our driving courses, lessons, and more.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-sm overflow-hidden">
          <Accordion
            type="single"
            collapsible
            value={expandedFAQ || undefined}
            onValueChange={(value) => setExpandedFAQ(value)}
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={`item-${index}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <AccordionItem value={`item-${index}`} className="border-b border-gray-200">
                  <AccordionTrigger className="px-6 py-4 hover:bg-gray-50 transition-colors text-left">
                    <span className="font-medium text-gray-900">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 py-4 text-gray-700 bg-gray-50">
                    <p>{faq.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>

        <div className="mt-12 max-w-2xl mx-auto text-center p-6 bg-drivewise-50 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Didn't find what you're looking for?
          </h2>
          <p className="text-gray-700 mb-6">
            Our support team is here to help you with any other questions you might have.
          </p>
          <a
            href="/contact"
            className="inline-block bg-drivewise-600 text-white px-6 py-3 rounded-md font-medium hover:bg-drivewise-700 transition-colors"
          >
            Contact them
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default FAQ;
